import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMfaRequirement } from '@/hooks/useMfaRequirement';

const MFA_SETUP_PATH = '/settings/security/mfa-setup';
const ALLOWED_PATHS = [
  MFA_SETUP_PATH,
  '/auth',
  '/login',
  '/signup',
  '/logout',
  '/terms',
  '/privacy',
  '/verify'
];

// SECURITY: Routes that MUST be blocked during grace period
const SENSITIVE_ROUTES = [
  '/admin',
  '/org',
  '/profile/dashboard',
  '/settings',
  '/vault',
  '/applications'
];

export function MfaEnforcementGuard({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { requiresMfa, isEnforcementRequired, isInGracePeriod, isLoading, hasMfaEnabled } = useMfaRequirement();

  // Skip MFA enforcement in test environments (Playwright)
  const isTestEnvironment = typeof window !== 'undefined' && 
    (window.navigator.userAgent.includes('HeadlessChrome') || 
     window.localStorage.getItem('PLAYWRIGHT_TEST') === 'true');

  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[MfaEnforcementGuard] Route check:', {
        pathname: location.pathname,
        requiresMfa,
        isInGracePeriod,
        isEnforcementRequired,
        isSensitive: SENSITIVE_ROUTES.some(route => location.pathname.startsWith(route)),
        willRedirect: isEnforcementRequired && !ALLOWED_PATHS.some(p => location.pathname.startsWith(p))
      });
    }
  }, [location.pathname, requiresMfa, isInGracePeriod, isEnforcementRequired]);

  useEffect(() => {
    // Skip all MFA enforcement in test environments
    if (isTestEnvironment) return;
    
    if (isLoading) return;

    const isAllowedPath = ALLOWED_PATHS.some(path => location.pathname.startsWith(path));
    const isSensitiveRoute = SENSITIVE_ROUTES.some(route => location.pathname.startsWith(route));

    // ENFORCEMENT: Block access after grace period expires
    // CRITICAL FIX: Don't redirect if user already has MFA enabled (prevents redirect loop)
    if (isEnforcementRequired && !hasMfaEnabled && !isAllowedPath) {
      console.warn('[MfaEnforcementGuard] Redirecting to MFA setup (enforcement):', {
        from: location.pathname,
        supportId: `mfa-block-${Date.now()}`
      });
      navigate(MFA_SETUP_PATH, { replace: true });
      return;
    }
    
    // CRITICAL FIX: If user has MFA enabled but is on setup page, redirect to dashboard
    if (hasMfaEnabled && location.pathname === MFA_SETUP_PATH) {
      console.info('[MfaEnforcementGuard] User has MFA, redirecting from setup page');
      navigate('/', { replace: true });
      return;
    }

    // GRACE PERIOD: Block sensitive routes but allow public pages
    // CRITICAL: Skip redirect if user already has verified MFA (fixes repeated warnings)
    if (requiresMfa && isInGracePeriod && !hasMfaEnabled && isSensitiveRoute && !isAllowedPath) {
      console.warn('[MfaEnforcementGuard] Redirecting to MFA setup (grace period):', {
        from: location.pathname,
        supportId: `mfa-grace-${Date.now()}`
      });
      navigate(MFA_SETUP_PATH, { 
        replace: true,
        state: { from: location.pathname }
      });
    }
  }, [isEnforcementRequired, requiresMfa, isInGracePeriod, location.pathname, navigate, isLoading, isTestEnvironment, hasMfaEnabled]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Skip all blocking in test environments
  if (isTestEnvironment) {
    return <>{children}</>;
  }

  // Allow MFA setup and public pages
  if (ALLOWED_PATHS.some(path => location.pathname.startsWith(path))) {
    return <>{children}</>;
  }

  // Block access if enforcement is required
  if (isEnforcementRequired && !hasMfaEnabled) {
    return null;
  }

  // Show grace period warning for sensitive routes (only if MFA not yet enabled)
  if (requiresMfa && isInGracePeriod && !hasMfaEnabled) {
    const isSensitiveRoute = SENSITIVE_ROUTES.some(route => location.pathname.startsWith(route));
    
    if (isSensitiveRoute) {
      return null;
    }
  }

  return <>{children}</>;
}
